/**
 * System Prompt Configuration component for administrators.
 *
 * Features:
 * - List all system prompts
 * - Create new prompts
 * - Edit existing prompts
 * - Set default prompt
 * - Deactivate prompts
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Edit2,
  Trash2,
  Star,
  Check,
  X,
  Loader2,
  AlertCircle,
  History,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import {
  listPrompts,
  createPrompt,
  updatePrompt,
  deletePrompt,
  setDefaultPrompt,
  listPromptVersions,
  SystemPrompt,
  SystemPromptCreate,
  SystemPromptUpdate,
  PromptVersion,
} from "../../lib/api/writingAssistant";

/**
 * Props for SystemPromptConfig component.
 */
export interface SystemPromptConfigProps {
  /** Whether to show inactive prompts */
  showInactive?: boolean;
}

/**
 * Form state for creating/editing prompts.
 */
interface PromptForm {
  name: string;
  prompt_text: string;
  description: string;
  is_default: boolean;
}

const initialFormState: PromptForm = {
  name: "",
  prompt_text: "",
  description: "",
  is_default: false,
};

/**
 * Admin component for managing system prompts.
 */
export function SystemPromptConfig({
  showInactive = true,
}: SystemPromptConfigProps) {
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PromptForm>(initialFormState);
  const [error, setError] = useState<string | null>(null);
  const [viewingHistoryId, setViewingHistoryId] = useState<string | null>(null);
  const [expandedVersionId, setExpandedVersionId] = useState<string | null>(null);

  // Fetch prompts - refetch on mount to ensure fresh data when navigating back
  const {
    data: prompts,
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ["system-prompts", showInactive],
    queryFn: () => listPrompts(showInactive),
    refetchOnMount: "always",
  });

  // Fetch version history for a specific prompt - refetch when enabled
  const {
    data: versions,
    isLoading: versionsLoading,
  } = useQuery({
    queryKey: ["prompt-versions", viewingHistoryId],
    queryFn: () => viewingHistoryId ? listPromptVersions(viewingHistoryId) : Promise.resolve([]),
    enabled: !!viewingHistoryId,
    refetchOnMount: "always",
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: SystemPromptCreate) => createPrompt(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["system-prompts"] });
      setIsCreating(false);
      setForm(initialFormState);
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to create prompt");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: SystemPromptUpdate;
    }) => updatePrompt(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["system-prompts"] });
      queryClient.invalidateQueries({ queryKey: ["prompt-versions", variables.id] });
      setEditingId(null);
      setForm(initialFormState);
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to update prompt");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePrompt(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["system-prompts"] });
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to delete prompt");
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => setDefaultPrompt(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["system-prompts"] });
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to set default prompt");
    },
  });

  /**
   * Handle form input changes.
   */
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Handle checkbox changes.
   */
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  };

  /**
   * Start creating a new prompt.
   */
  const handleStartCreate = () => {
    setIsCreating(true);
    setEditingId(null);
    setForm(initialFormState);
    setError(null);
  };

  /**
   * Start editing a prompt.
   */
  const handleStartEdit = (prompt: SystemPrompt) => {
    setEditingId(prompt.id);
    setIsCreating(false);
    setForm({
      name: prompt.name,
      prompt_text: prompt.prompt_text,
      description: prompt.description || "",
      is_default: prompt.is_default,
    });
    setError(null);
  };

  /**
   * Cancel editing/creating.
   */
  const handleCancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setForm(initialFormState);
    setError(null);
  };

  /**
   * Submit the form.
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name.trim() || !form.prompt_text.trim()) {
      setError("Name and prompt text are required");
      return;
    }

    if (isCreating) {
      createMutation.mutate({
        name: form.name.trim(),
        prompt_text: form.prompt_text.trim(),
        description: form.description.trim() || undefined,
        is_default: form.is_default,
      });
    } else if (editingId) {
      updateMutation.mutate({
        id: editingId,
        data: {
          name: form.name.trim(),
          prompt_text: form.prompt_text.trim(),
          description: form.description.trim() || undefined,
        },
      });
    }
  };

  /**
   * Handle delete prompt.
   */
  const handleDelete = (prompt: SystemPrompt) => {
    if (prompt.is_default) {
      setError("Cannot delete the default prompt");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to deactivate "${prompt.name}"? This action can be undone by an administrator.`
      )
    ) {
      deleteMutation.mutate(prompt.id);
    }
  };

  /**
   * Handle set as default.
   */
  const handleSetDefault = (prompt: SystemPrompt) => {
    if (!prompt.is_active) {
      setError("Cannot set an inactive prompt as default");
      return;
    }
    setDefaultMutation.mutate(prompt.id);
  };

  /**
   * Handle view version history.
   */
  const handleViewHistory = (promptId: string) => {
    if (viewingHistoryId === promptId) {
      setViewingHistoryId(null);
      setExpandedVersionId(null);
    } else {
      setViewingHistoryId(promptId);
      setExpandedVersionId(null);
    }
  };

  /**
   * Toggle version expansion in history view.
   */
  const toggleVersionExpansion = (versionId: string) => {
    setExpandedVersionId(expandedVersionId === versionId ? null : versionId);
  };

  /**
   * Get the prompt being viewed for history.
   */
  const getPromptForHistory = () => {
    return prompts?.find(p => p.id === viewingHistoryId);
  };

  const isFormBusy =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    setDefaultMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-brand-muted" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="rounded-card bg-brand-error/5 p-4 text-brand-error">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <span>Failed to load prompts</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-brand-text">
            System Prompts
          </h2>
          <p className="mt-1 text-sm text-brand-muted">
            Configure the system prompts used for text enhancement
          </p>
        </div>
        {!isCreating && !editingId && (
          <Button onClick={handleStartCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            New Prompt
          </Button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="flex items-center gap-2 rounded-card bg-brand-error/5 p-4 text-brand-error">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-brand-error hover:opacity-70"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Create/Edit Form */}
      {(isCreating || editingId) && (
        <Card className="p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <h3 className="font-medium text-brand-text">
              {isCreating ? "Create New Prompt" : "Edit Prompt"}
            </h3>

            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-brand-text"
              >
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={form.name}
                onChange={handleInputChange}
                disabled={isFormBusy}
                className="mt-1 block w-full rounded-input border border-brand-border px-3 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:bg-brand-surface"
                placeholder="e.g., Professional Enhancement"
              />
            </div>

            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-brand-text"
              >
                Description
              </label>
              <input
                type="text"
                id="description"
                name="description"
                value={form.description}
                onChange={handleInputChange}
                disabled={isFormBusy}
                className="mt-1 block w-full rounded-input border border-brand-border px-3 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:bg-brand-surface"
                placeholder="Brief description of what this prompt does"
              />
            </div>

            <div>
              <label
                htmlFor="prompt_text"
                className="block text-sm font-medium text-brand-text"
              >
                Prompt Text
              </label>
              <textarea
                id="prompt_text"
                name="prompt_text"
                value={form.prompt_text}
                onChange={handleInputChange}
                disabled={isFormBusy}
                rows={6}
                className="mt-1 block w-full rounded-input border border-brand-border px-3 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:bg-brand-surface"
                placeholder="Enter the system prompt that will be used to enhance text..."
              />
              <p className="mt-1 text-xs text-brand-muted">
                This prompt will be sent to the LLM before the user's text
              </p>
            </div>

            {isCreating && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_default"
                  name="is_default"
                  checked={form.is_default}
                  onChange={handleCheckboxChange}
                  disabled={isFormBusy}
                  className="h-4 w-4 rounded border-brand-border text-brand-primary focus:ring-brand-primary"
                />
                <label
                  htmlFor="is_default"
                  className="text-sm font-medium text-brand-text"
                >
                  Set as default prompt
                </label>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isFormBusy}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isFormBusy}>
                {isFormBusy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : isCreating ? (
                  "Create Prompt"
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Prompts List */}
      <div className="space-y-3">
        {prompts?.length === 0 ? (
          <div className="rounded-card border border-dashed border-brand-border p-8 text-center">
            <p className="text-brand-muted">No prompts configured yet</p>
            <Button onClick={handleStartCreate} className="mt-4 gap-2">
              <Plus className="h-4 w-4" />
              Create First Prompt
            </Button>
          </div>
        ) : (
          prompts?.map((prompt) => (
            <Card
              key={prompt.id}
              className={`p-4 ${!prompt.is_active ? "opacity-60" : ""}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-brand-text truncate">
                      {prompt.name}
                    </h4>
                    {prompt.is_default && (
                      <span className="inline-flex items-center gap-1 rounded-pill bg-brand-warning/10 px-2 py-0.5 text-xs font-medium text-brand-warning">
                        <Star className="h-3 w-3" />
                        Default
                      </span>
                    )}
                    {!prompt.is_active && (
                      <span className="inline-flex items-center gap-1 rounded-pill bg-brand-surface px-2 py-0.5 text-xs font-medium text-brand-muted">
                        Inactive
                      </span>
                    )}
                  </div>
                  {prompt.description && (
                    <p className="mt-1 text-sm text-brand-muted">
                      {prompt.description}
                    </p>
                  )}
                  <p className="mt-2 text-sm text-brand-muted line-clamp-2">
                    {prompt.prompt_text}
                  </p>
                  <p className="mt-2 text-xs text-brand-muted">
                    Updated{" "}
                    {new Date(prompt.updated_at).toLocaleDateString()}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleViewHistory(prompt.id)}
                    disabled={isFormBusy}
                    className={`rounded p-1.5 ${viewingHistoryId === prompt.id ? "bg-brand-primary/10 text-brand-primary" : "text-brand-muted hover:bg-brand-surface hover:text-brand-primary"} disabled:opacity-50`}
                    title="View history"
                  >
                    <History className="h-4 w-4" />
                  </button>
                  {prompt.is_active && !prompt.is_default && (
                    <button
                      onClick={() => handleSetDefault(prompt)}
                      disabled={isFormBusy}
                      className="rounded p-1.5 text-brand-muted hover:bg-brand-surface hover:text-brand-warning disabled:opacity-50"
                      title="Set as default"
                    >
                      <Star className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleStartEdit(prompt)}
                    disabled={isFormBusy || editingId === prompt.id}
                    className="rounded p-1.5 text-brand-muted hover:bg-brand-surface hover:text-brand-teal disabled:opacity-50"
                    title="Edit"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  {!prompt.is_default && prompt.is_active && (
                    <button
                      onClick={() => handleDelete(prompt)}
                      disabled={isFormBusy}
                      className="rounded p-1.5 text-brand-muted hover:bg-brand-error/5 hover:text-brand-error disabled:opacity-50"
                      title="Deactivate"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Version History Panel */}
              {viewingHistoryId === prompt.id && (
                <div className="mt-4 border-t border-brand-border pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="text-sm font-medium text-brand-text flex items-center gap-2">
                      <History className="h-4 w-4" />
                      Version History
                    </h5>
                    <button
                      onClick={() => setViewingHistoryId(null)}
                      className="text-brand-muted hover:text-brand-text"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  {versionsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-brand-muted" />
                    </div>
                  ) : !versions || versions.length === 0 ? (
                    <p className="text-sm text-brand-muted italic py-2">
                      No version history available. History is created when the prompt is edited.
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {versions.map((version) => (
                        <div
                          key={version.id}
                          className="border border-brand-border rounded-input bg-brand-surface"
                        >
                          <button
                            onClick={() => toggleVersionExpansion(version.id)}
                            className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-brand-bg"
                          >
                            <div className="flex items-center gap-2">
                              {expandedVersionId === version.id ? (
                                <ChevronDown className="h-4 w-4 text-brand-muted" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-brand-muted" />
                              )}
                              <span className="text-sm font-medium text-brand-text">
                                v{version.version_number}
                              </span>
                              <span className="text-sm text-brand-muted">
                                - {version.name}
                              </span>
                            </div>
                            <span className="text-xs text-brand-muted">
                              {new Date(version.changed_at).toLocaleString()}
                            </span>
                          </button>

                          {expandedVersionId === version.id && (
                            <div className="px-3 pb-3 border-t border-brand-border">
                              {version.description && (
                                <p className="mt-2 text-sm text-brand-muted">
                                  {version.description}
                                </p>
                              )}
                              <div className="mt-2">
                                <label className="text-xs font-medium text-brand-muted uppercase">
                                  Prompt Text
                                </label>
                                <pre className="mt-1 text-xs text-brand-muted bg-brand-bg p-2 rounded border border-brand-border whitespace-pre-wrap max-h-[200px] overflow-y-auto">
                                  {version.prompt_text}
                                </pre>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

export default SystemPromptConfig;
